import React, { useEffect, useMemo, useRef, useState } from "react";
import ExecutionEntryForm, {
  getExecutionEntryFormSchema,
  ExecutionEntryFormValues,
} from "./execution-entry-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ApiResponse, MaintenanceExecutionEntry } from "@/lib/types";
import { api } from "@/lib/api";
import {
  useCreateMaintenanceExecutionEntryAttachment,
  useDeleteMaintenanceExecutionEntryAttachment,
  useUpdateMaintenanceExecutionEntry,
} from "@/services/mutations/maintenance";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";
import { Step } from "./execution-entry-form/constants";
import { useGetTaskById } from "@/services/queries/maintenance";

const EditExecutionEntryForm = ({
  open,
  setOpen,
  executionEntry,
  vesselId,
  taskId,
  refetch,
  refetchTask,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  executionEntry: MaintenanceExecutionEntry;
  vesselId: string;
  taskId: string;
  refetch: () => void;
  refetchTask: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<number>(Step.INITIAL);
  const { data: task } = useGetTaskById({ vesselId, id: taskId });
  const { mutateAsync: createExecutionEntryAttachment } =
    useCreateMaintenanceExecutionEntryAttachment();
  const { mutateAsync: deleteExecutionEntryAttachment } =
    useDeleteMaintenanceExecutionEntryAttachment();

  // Hard guard against double-submit: a synchronous ref blocks duplicate requests even when rapid taps fire before React re-renders. Cleared only when the whole flow settles.
  const isSubmittingRef = useRef(false);

  const { mutate: updateExecutionEntry, isPending: isUpdatingEntry } =
    useUpdateMaintenanceExecutionEntry({
      onSuccess: async ({ id }) => {
        if (form.getValues("files")) {
          const deletedAttachments = (executionEntry.attachments ?? []).filter(
            (attachment) =>
              !form
                .watch("files")
                .some((file) => (file as { id: string }).id === attachment.id)
          );

          const newAttachments = form
            .watch("files")
            .filter((file) => file instanceof File);

          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // entry was already created, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled([
            ...newAttachments.map((file) =>
              createExecutionEntryAttachment({
                file,
                vesselId,
                taskId,
                executionEntryId: id,
              })
            ),
            ...(deletedAttachments ?? []).map((attachment) =>
              deleteExecutionEntryAttachment({
                vesselId,
                taskId,
                executionEntryId: id,
                attachmentId: attachment.id,
              })
            ),
          ]);
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }

          refetch();
          setOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["maintenance-execution-entries"],
          });
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_execution_entry_description"),
          });

          Mixpanel.track("Execution Entry Edited Successfully");
        } else {
          refetch();
          setOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["maintenance-execution-entries"],
          });
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_execution_entry_description"),
          });

          Mixpanel.track("Execution Entry Edited Successfully");
        }

        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Execution Entry Edit Failed");
        isSubmittingRef.current = false;
      },
    });

  const defaultValues: Partial<ExecutionEntryFormValues> = useMemo(() => {
    return {
      executionDate: executionEntry?.executionDate
        ? new Date(executionEntry.executionDate)
        : new Date(),
      runningHoursAtExecution: executionEntry?.runningHoursAtExecution,
      notes: executionEntry?.notes ?? "",
      files:
        (executionEntry?.attachments ?? [])
          .map((attachment) => {
            return { id: attachment.id, url: attachment.fileName };
          })
          .filter((file) => file) || [],
    };
  }, [executionEntry]);

  const form = useForm<ExecutionEntryFormValues>({
    resolver: zodResolver(
      getExecutionEntryFormSchema(task?.taskDetail.scheduleType)
    ),
    defaultValues,
  });

  const handleUpdateExecutionEntry = (values: ExecutionEntryFormValues) => {
    updateExecutionEntry({
      id: executionEntry.id,
      vesselId,
      taskId,
      executionDate: values.executionDate,
      runningHoursAtExecution: values.runningHoursAtExecution!,
      notes: values.notes,
      version: executionEntry.version ?? 0,
    });
  };

  const onSubmit = (values: ExecutionEntryFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    handleUpdateExecutionEntry(values);
  };

  useEffect(() => {
    const getPreviewUrls = async () => {
      const files = executionEntry.attachments;

      if (!files || files.length === 0) {
        return;
      }

      try {
        const urlResults = await Promise.all(
          files.map(async (file) => {
            const res = await api.get<ApiResponse<string>>(
              `/vessels/${vesselId}/maintenance/tasks/${taskId}/execution-entries/attachments/${file.id}/url`
            );
            return { id: file.id, url: res.data.value };
          })
        );
        form.setValue("files", urlResults);
      } catch (error) {
        form.setValue("files", []);
        console.error("Error fetching attachment URLs:", error);
      }
    };

    if (open) {
      getPreviewUrls();
    }
  }, [
    executionEntry.attachments,
    executionEntry.id,
    form,
    open,
    vesselId,
    taskId,
  ]);

  useEffect(() => {
    form.reset({
      ...defaultValues,
    });
  }, [form, defaultValues]);

  return (
    <>
      <ExecutionEntryForm
        title={t("title.edit_execution_entry")}
        open={open}
        setOpen={setOpen}
        form={form}
        step={step}
        setStep={setStep}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isSubmitting={isUpdatingEntry}
        scheduleType={task?.taskDetail.scheduleType}
        task={task}
        vesselId={vesselId}
        onTaskUpdated={refetchTask}
      />
    </>
  );
};

export default EditExecutionEntryForm;
